const toError = (error: unknown): Error =>
	error instanceof Error ? error : new Error(String(error))

/**
 * Janus renegotiations (createOffer and the answer to it) as promises that
 * always settle. Janus calls no callback at all once its handle is being
 * destroyed, so a caller waiting for one would wait forever: the trainer's
 * recording stop did exactly that when a room change closed the call
 * (trainings-dev, 2026-10-09).
 *
 * Each renegotiation fails after `timeoutMs`, and all of them fail at once
 * when the connection closes (`abortAll`). Callbacks arriving afterwards are
 * ignored.
 */
export class RenegotiationGuard {
	private readonly pending = new Set<(error: Error) => void>()
	private closedReason: string | null = null

	constructor(private readonly timeoutMs: number) {}

	run<T>(
		label: string,
		start: (
			resolve: (value: T) => void,
			reject: (error: unknown) => void
		) => void
	): Promise<T> {
		return new Promise<T>((resolve, reject) => {
			if (this.closedReason) {
				reject(new Error(`${label}: ${this.closedReason}`))
				return
			}

			let settled = false
			const settle = (finish: () => void) => {
				if (settled) return
				settled = true
				clearTimeout(timer)
				this.pending.delete(abort)
				finish()
			}
			const abort = (error: Error) => settle(() => reject(error))
			const timer = setTimeout(
				() =>
					abort(
						new Error(
							`${label}: Janus did not answer within ${this.timeoutMs} ms`
						)
					),
				this.timeoutMs
			)
			this.pending.add(abort)

			try {
				start(
					value => settle(() => resolve(value)),
					error => abort(toError(error))
				)
			} catch (error) {
				abort(toError(error))
			}
		})
	}

	/** The connection is closing: everything still waiting fails now. */
	abortAll(reason: string): void {
		this.closedReason = reason
		for (const abort of [...this.pending]) {
			abort(new Error(reason))
		}
	}
}
